// IniReader + IniScanf — the ini parse layer every InitClass/Level loader runs through.
// mpshared/ini.cpp. The reader consumes the crunched-ini's ordered {sections[{name,
// props[{key,value}]}]} JSON (sim/tools/extract-ini.mjs); FindSection/FindProperty are
// linear scans in stored order, exactly like the C++ over the crunched layout.
//
// IniScanf is the CUSTOM scanf (mpshared/ini.cpp:34-244), NOT libc sscanf:
//   %s  — reads until the next LITERAL char in the format (or NUL), skipping leading and
//         trimming trailing whitespace;  %s+ also yields the start offset
//   %a  — {brace-delimited} argument group
//   %d  — optional whitespace, optional '.', optional '-', up to 10 digit chars, optional
//         trailing '%' consumed; atoi
//   %ld — same, atol (32-bit here)
//   %+  — yields the current parse offset
//   %*  — suppress assignment
//   whitespace in the format eats input whitespace; other format chars must match exactly.
// Returns the assigned-argument count; parsed values are returned as an array.

export interface IniProp {
  key: string;
  value: string;
}
export interface IniSection {
  name: string;
  props: IniProp[];
}

const isWhite = (c: string) => c === ' ' || c === '\t' || c === '\n' || c === '\r';
const isDigit = (c: string) => c >= '0' && c <= '9';

/** The scanf core. Returns { count, values } — `values` holds each assigned arg in order
 *  (numbers for %d/%ld/%+/%s+'s offset, strings for %s/%a). */
export function IniScanf(
  buff: string,
  fmt: string,
): { count: number; values: (number | string)[] } {
  let bi = 0; // pszBuffT
  let fi = 0; // pszFmtT
  const values: (number | string)[] = [];
  let count = 0;

  while (fi < fmt.length) {
    // %+ — position capture (checked before end-of-buffer)
    if (fmt[fi] === '%' && fmt[fi + 1] === '+') {
      fi += 2;
      values.push(bi);
      count++;
      continue;
    }

    const f = fmt[fi];
    if (f === ' ' || f === '\n' || f === '\t') {
      while (bi < buff.length && isWhite(buff[bi])) bi++;
      fi++;
      continue;
    }

    if (bi >= buff.length) break; // end of buffer

    if (f === '%') {
      fi++;
      let fSuppress = false;
      if (fmt[fi] === '*') {
        fSuppress = true;
        fi++;
      }

      switch (fmt[fi]) {
        case 's': {
          fi++;
          let fSaveLocation = false;
          if (fmt[fi] === '+') {
            fSaveLocation = true;
            fi++;
          }

          let nchStart = bi;
          while (bi < buff.length && isWhite(buff[bi])) {
            nchStart++;
            bi++;
          }

          const chStop = fi < fmt.length ? fmt[fi] : '\0';
          let out = '';
          let whiteFrom = -1; // pszWhiteLast (index into out)
          while (bi < buff.length && buff[bi] !== chStop) {
            if (!isWhite(buff[bi])) whiteFrom = -1;
            else if (whiteFrom === -1) whiteFrom = out.length;
            if (!fSuppress) out += buff[bi];
            bi++;
          }
          if (!fSuppress) {
            if (whiteFrom !== -1) out = out.slice(0, whiteFrom);
            values.push(out);
            count++;
          }
          if (!fSuppress && fSaveLocation) {
            values.push(nchStart);
            count++;
          }
          continue;
        }

        case 'a': {
          fi++;
          bi++; // past '{' (asserted in C++)
          let out = '';
          while (bi < buff.length && buff[bi] !== '}') {
            if (!fSuppress) out += buff[bi];
            bi++;
          }
          if (!fSuppress) {
            values.push(out);
            count++;
          }
          bi++; // past '}'
          continue;
        }

        case 'l':
          fi++;
        // falls through to 'd' (C++ asserts the next char is 'd')
        // eslint-disable-next-line no-fallthrough
        case 'd': {
          fi++;
          while (bi < buff.length && isWhite(buff[bi])) bi++;
          if (buff[bi] === '.') bi++;
          let num = '';
          if (buff[bi] === '-') {
            num = '-';
            bi++;
          }
          let fHaveInt = false;
          // up to 10 chars total in szT (incl. the '-')
          while (num.length < 10 && bi < buff.length && isDigit(buff[bi])) {
            num += buff[bi++];
            fHaveInt = true;
          }
          if (fHaveInt) {
            if (buff[bi] === '%') bi++;
            if (!fSuppress) {
              values.push(parseInt(num, 10) | 0); // atoi/atol, 32-bit
              count++;
            }
          }
          continue;
        }

        default:
          return { count, values }; // unrecognized % command
      }
    }

    // literal match
    if (buff[bi] === fmt[fi]) {
      bi++;
      fi++;
      continue;
    }
    break;
  }

  return { count, values };
}

export class IniReader {
  private sections: IniSection[];

  constructor(sections: IniSection[]) {
    this.sections = sections;
  }

  // mpshared/ini.cpp:346-357 — linear scan, exact name match, stored order
  private FindSection(pszSec: string): IniSection | null {
    for (const s of this.sections) if (s.name === pszSec) return s;
    return null;
  }

  // mpshared/ini.cpp:358+ — first key match in property order
  private FindProperty(psec: IniSection, pszProp: string): string | null {
    for (const p of psec.props) if (p.key === pszProp) return p.value;
    return null;
  }

  /** GetPropertyValue(sec, prop, value-out) — the raw-string overload (ini.cpp:286-296). */
  GetPropertyString(pszSec: string, pszProp: string): string | null {
    const psec = this.FindSection(pszSec);
    if (psec === null) return null;
    return this.FindProperty(psec, pszProp);
  }

  /** GetPropertyValue(sec, prop, fmt, ...) — the scanf overload (ini.cpp:298-311). */
  GetPropertyValue(
    pszSec: string,
    pszProp: string,
    pszFmt: string,
  ): { count: number; values: (number | string)[] } {
    const v = this.GetPropertyString(pszSec, pszProp);
    if (v === null) return { count: 0, values: [] };
    return IniScanf(v, pszFmt);
  }

  /** Convenience for the ubiquitous single-%d read: null when absent/unparsed. */
  GetInt(pszSec: string, pszProp: string): number | null {
    const r = this.GetPropertyValue(pszSec, pszProp, '%d');
    return r.count === 1 ? (r.values[0] as number) : null;
  }

  /** The whole ordered section (the Level loader walks GameObjects/Galaxite raw). */
  GetSection(pszSec: string): IniSection | null {
    return this.FindSection(pszSec);
  }

  /** Convenience for the ubiquitous string read: null when absent. */
  GetString(pszSec: string, pszProp: string): string | null {
    return this.GetPropertyString(pszSec, pszProp);
  }

  /** FindNextProperty cursor (ini.cpp:313-331): iterate a section's properties in order. */
  *Properties(pszSec: string): Generator<IniProp> {
    const psec = this.FindSection(pszSec);
    if (psec === null) return;
    for (const p of psec.props) yield p;
  }
}
